import { Injectable, inject } from '@angular/core';
import { DirtyCheckPlugin, Query } from '@datorama/akita';
import { NoteListState, NoteListStore } from './note-list.store';

/**
 * NoteListQuery - Query for Notes Panel V2 state.
 *
 * Provides:
 * - Selectors for notes and editor state
 * - Dirty checking for unsaved changes integration with QuickActionsUnsavedChangesService
 *
 * Note: We use Akita's DirtyCheckPlugin directly with `watchProperty` to only track
 * editor fields (editorContent, editingNoteGuid), not the notes array. This avoids
 * issues with DirtyChecker2's comparator which doesn't handle arrays correctly.
 */
@Injectable({ providedIn: 'root' })
export class NoteListQuery extends Query<NoteListState> {
  protected store: NoteListStore;

  /**
   * Dirty checker that only tracks editor-related fields.
   * The `notes` array is excluded since it's cached data, not user input.
   */
  private readonly _dirtyChecker = new DirtyCheckPlugin<NoteListState>(this, {
    watchProperty: ['editorContent', 'editingNoteGuid', 'selectedTagGuids'],
  });

  /**
   * Compatibility wrapper for QuickActionsUnsavedChangesService which expects
   * `dirtyChecker2.isDirty$` and `dirtyChecker2.dirtyChecker.reset()/setHead()`.
   * Also exposes `isDirty()` method for tests.
   */
  dirtyChecker2 = {
    isDirty$: this._dirtyChecker.isDirty$,
    isDirty: () => this._dirtyChecker.isDirty(),
    dirtyChecker: this._dirtyChecker,
  };

  constructor() {
    const store = inject(NoteListStore);

    super(store);
    this.store = store;

    this._dirtyChecker.setHead();
  }

  /** Select notes collection */
  notes$ = this.select((state) => state.notes);

  /** Select editor content */
  editorContent$ = this.select((state) => state.editorContent);

  /** Select editing note GUID */
  editingNoteGuid$ = this.select((state) => state.editingNoteGuid);

  /** Select whether editing a legacy note */
  isEditingLegacyNote$ = this.select((state) => state.isEditingLegacyNote);

  /** Select entity ID */
  entityId$ = this.select((state) => state.entityId);
}
